import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { useI18N, usePageTitle, useResponsive } from "@sundae/react-hooks";
import { Button, Separator, Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useCallback, useEffect, useMemo } from "react";

import isEmpty from "lodash/isEmpty";
import FadeInBasic from "../../components/FadeInView/FadeInBasic";
import NoDataIndicator from "../../components/NoDataIndicator";
import PageContent from "../../components/PageContent";
import MaybeTasteTestReadOnlyStatusOverlay from "../../components/TasteTestComponents/MaybeTasteTestReadOnlyStatusOverlay";
import TasteTestBannerContainer from "../../components/TasteTestComponents/TasteTestBannerContainer";
import TasteTestDetailBanner from "../../components/TasteTestComponents/TasteTestDetailBanner";
import TasteTestPfp from "../../components/TasteTestComponents/TasteTestPfp";
import TasteTestPriceChartTabView from "../../components/TasteTestComponents/TasteTestPriceChartTabView";
import TasteTestProjectDetails from "../../components/TasteTestComponents/TasteTestProjectDetails";
import TasteTestTokenomics from "../../components/TasteTestComponents/TasteTestTokenomics";
import TasteTestTransactionWidget from "../../components/TasteTestComponents/TasteTestTransactionWidget";
import { TASTE_TEST_OVERVIEW_TABLES_ID, getPageTitle } from "../../constants/page.constants";
import useAppNavigate from "../../hooks/router/useAppNavigate";
import { useLatestTransactionToast } from "../../hooks/taste-test/useLatestTransactionToast";
import Footer from "../../layouts/Footer";
import { OrderActions, useOrderContext } from "../../stores/OrderContext";
import { getNoDataTableImageSize } from "../../utils/images.utils";
import { createTasteTestUrl } from "../../utils/taste-test.utils";
import { paths } from "../routes";
import TasteTestDetailSkeleton from "./TasteTestDetail.skeleton";
import { useTasteTestContext } from "./context";

const TasteTestDetail: FC = () => {
  const navigate = useAppNavigate();
  const { t } = useI18N("taste-test");
  const responsive = useResponsive();
  const { dispatch } = useOrderContext();
  const {
    state: { error, tasteTest, isLoading },
  } = useTasteTestContext();

  const handleLaunchTasteTest = useCallback(() => {
    window.open(createTasteTestUrl, "_self");
  }, []);

  const handleNavigateBackToOverview = useCallback(
    () => navigate({ to: paths.tasteTest.root, hash: TASTE_TEST_OVERVIEW_TABLES_ID }),
    []
  );

  const memoizedTasteTestContent = useMemo(() => {
    if (isLoading) {
      return <TasteTestDetailSkeleton />;
    }

    if (!isEmpty(tasteTest)) {
      return (
        <FadeInBasic effects={{ fadeIn: true, scale: false, slideUp: false }}>
          {/* -------------------------------------------------------------------------------------------------
           * Banner Image
           * -----------------------------------------------------------------------------------------------*/}
          <TasteTestBannerContainer />

          <div className="mx-auto flex max-w-6xl flex-col gap-8">
            {/* -------------------------------------------------------------------------------------------------
             * Personalized Banner information
             * -----------------------------------------------------------------------------------------------*/}
            <TasteTestPfp />
            <TasteTestDetailBanner />

            <div className="relative z-10 grid gap-8">
              {/* -------------------------------------------------------------------------------------------------
               * Container for Campaign Details and Buying Section
               * - Structure on mobile:
               *  1. Banner Image
               *  2. Taste Test Banner with KPIs
               *  3. Contribution Widget
               *  4. Contributions Table
               *  5. Price Chart
               *  6. Team
               *  7. Tokenomics
               *
               * - Structure on LG Screen:
               *  1. Banner Image
               *  2. Taste Test Banner with KPIs
               *  3. Price Chart
               *  4. Team
               *  5. Tokenomics
               *  6. Contribution Section (Widget and Table Sticky on the right)
               * -----------------------------------------------------------------------------------------------*/}

              <div className="relative flex w-full flex-col gap-8 lg:grid lg:grid-cols-6 xl:grid-cols-5">
                {responsive.lg ? (
                  <>
                    <div className="flex h-full flex-col gap-8 lg:col-span-3">
                      <TasteTestPriceChartTabView />
                      <Separator
                        orientation="horizontal"
                        className={toolkitCx.layout.background.separator}
                      />
                      <TasteTestProjectDetails />
                      <Separator
                        orientation="horizontal"
                        className={toolkitCx.layout.background.separator}
                      />
                      <TasteTestTokenomics />
                    </div>
                    <div className="lg:col-span-3 xl:col-span-2">
                      <TasteTestTransactionWidget />
                    </div>
                  </>
                ) : (
                  <>
                    <TasteTestTransactionWidget />
                    <Separator
                      orientation="horizontal"
                      className={toolkitCx.layout.background.separator}
                    />
                    <TasteTestPriceChartTabView />
                    <Separator
                      orientation="horizontal"
                      className={toolkitCx.layout.background.separator}
                    />
                    <TasteTestProjectDetails />
                    <Separator
                      orientation="horizontal"
                      className={toolkitCx.layout.background.separator}
                    />
                    <TasteTestTokenomics />
                  </>
                )}
              </div>

              {/* -------------------------------------------------------------------------------------------------
               * Footer
               * -----------------------------------------------------------------------------------------------*/}
              <Footer
                className="mb-4 mt-8 border-t border-t-gray-300 pt-8 dark:border-t-gray-600 xl:mt-12 xl:pt-12"
                size="md"
              />
            </div>
          </div>
        </FadeInBasic>
      );
    }

    return null;
  }, [isLoading, tasteTest]);

  usePageTitle(
    getPageTitle(t("seo.tasteTest.detail", { ns: "home", name: tasteTest?.asset?.ticker }))
  );

  // Cleanup.
  useEffect(() => {
    return () => {
      OrderActions.resetOrderFlow(dispatch);
    };
  }, []);

  /* -------------------------------------------------------------------------------------------------
   * When navigating from the Taste Test Overview Page, scroll back to the top of the page.
   * -----------------------------------------------------------------------------------------------*/
  useEffect(() => {
    if (!isLoading) {
      scrollTo(0, 0); // scroll to top on mount
    }
  }, [isLoading]);

  /**
   * A toast notification is displayed when a new transaction is detected.
   */
  useLatestTransactionToast();

  /* -------------------------------------------------------------------------------------------------
   * Empty State (No Tate Test found for the provided policyId).
   * -----------------------------------------------------------------------------------------------*/
  if (!!error) {
    return (
      <FadeInBasic className="flex flex-col items-center">
        <NoDataIndicator
          containerClassName="!gap-4"
          imageMaxWidth={getNoDataTableImageSize(responsive)}
          context="tasteTestNotFound"
          type="table"
        />

        <Button onClick={handleNavigateBackToOverview} size="sm" variant="neutral">
          {t("detail.back")}
        </Button>
      </FadeInBasic>
    );
  }

  /* -------------------------------------------------------------------------------------------------
   * Happy path :-)
   * -----------------------------------------------------------------------------------------------*/
  return (
    <PageContent>
      <MaybeTasteTestReadOnlyStatusOverlay />

      {/* -------------------------------------------------------------------------------------------------
       * Back Button + Create Taste Test
       * -----------------------------------------------------------------------------------------------*/}
      <div
        className={cx("mx-auto max-w-6xl", {
          "motion-safe:animate-pulse": isLoading,
        })}
      >
        <div className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <Text
            className={cx(
              "group flex cursor-pointer items-center gap-2",
              toolkitCx.layout.transition.transform,
              toolkitCx.recipes.scaleWhenActive
            )}
            hoverable
            onClick={handleNavigateBackToOverview}
            role="button"
            size="sm"
            tag="div"
            variant="muted"
          >
            <ArrowLeftIcon
              className={cx(
                "h-3 w-3 group-hover:-translate-x-1",
                toolkitCx.layout.transition.transform
              )}
            />
            <Text hoverable size="xs" tag="p" variant="muted">
              {t("detail.back")}
            </Text>
          </Text>

          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 lg:justify-start">
            <Text size="xs" tag="p" variant="silent">
              {t("create")}
            </Text>

            <Tooltip
              trigger={
                <div>
                  <Button onClick={handleLaunchTasteTest} size="xs" variant="neutral">
                    <EnvelopeIcon className="h-3 w-3" />
                    {t("create", { context: "cta" })}
                  </Button>
                </div>
              }
              tooltipLabel={t("create", { context: "tooltip" })}
            />
          </div>
        </div>
      </div>

      {memoizedTasteTestContent}
    </PageContent>
  );
};

export default TasteTestDetail;
